import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';

import { UsersActions } from '@modules/company-manager/users/application';
import {
  approvalsCountFailureAction,
  ApprovesActions,
  approvalsCountSuccessAction,
} from '@modules/regular-user/approves/application';
import { approvalsCountLoadingSelector } from '@modules/regular-user/approves/application';

import {
  getMyRequestsCountFailureAction,
  getMyRequestsCountSuccessAction,
  RequestsActions,
} from '@modules/regular-user/requests/application';
import { myRequestsCountLoadingSelector } from '@modules/regular-user/requests/application';
import { companyByIdSuccessSelector } from '@modules/super-admin/company/application';
import { Company } from '@modules/super-admin/company/domain';

import { HomeModules } from '@utils/home-modules';

@Component({
  selector: 'regular-user-home',
  templateUrl: './regular-user.container.html',
  styleUrls: ['./regular-user.container.scss'],
})
export class RegularUserContainer implements OnInit, AfterViewInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();

  modules = HomeModules;

  requestsCount: any = '';
  requestsLoading$: Observable<boolean | undefined>;

  approverCount: any = '';
  approvesLoading$: Observable<boolean | undefined>;

  isExpired: boolean = false;
  constructor(
    private store$: Store,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private router: Router
  ) {
    this.requestsLoading$ = this.store$.select(myRequestsCountLoadingSelector);

    this.approvesLoading$ = this.store$.select(approvalsCountLoadingSelector);

    this.store$
      .select(companyByIdSuccessSelector)
      .pipe(
        map((company) => {
          if (company) {
            const userData = JSON.parse(localStorage.getItem('s_user') || '{}');
            const isBypassed = userData?.isSubscriptionBypassed || userData?.company?.isSubscriptionBypassed || company[0]?.isSubscriptionBypassed;
            this.isExpired = isBypassed ? false : (company[0].subscription?.isExpired || false);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.onRequestsCount();
    this.onApprovesCount();
    localStorage.setItem('s_admin', 'false');
    this.onFailureActions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.getUserId();
    }, 10);
  }

  getMyRequestsCount(userId: string) {
    this.store$.dispatch(
      RequestsActions.getMyRequestsCount({ userId: userId })
    );
  }

  getApprovalsCount(userId: string) {
    this.store$.dispatch(
      ApprovesActions.approvalsCount({
        populate: ['approver', 'requester', 'approvals'],
        filter: {
          approvals: {
            $elemMatch: {
              _approver: userId,
              status: 'pending',
            },
          },
        },
      })
    );
  }

  getUserId() {
    let userData: any = JSON.parse(localStorage.getItem('s_user') || '{}');
    if (userData.id) {
      this.getApprovalsCount(userData.id);
      this.getMyRequestsCount(userData.id);
    }
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(approvalsCountFailureAction, getMyRequestsCountFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  changeToAdmin() {
    this.store$.dispatch(
      UsersActions.switchToAdmin({
        status: false,
      })
    );
    localStorage.setItem('s_admin', 'true');
    this.router.navigateByUrl(`/dashboard/users`);
  }
  onRequestsCount() {
    this.actions$
      .pipe(
        ofType(getMyRequestsCountSuccessAction),
        map((action) => {
          this.requestsCount = action.response.data.toLocaleString('en-US', {});
          return action.response;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  onApprovesCount() {
    this.actions$
      .pipe(
        ofType(approvalsCountSuccessAction),
        map((action) => {
          this.approverCount = action.response.data.toLocaleString('en-US');
          return action.response;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
}
